import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, format, isToday } from 'date-fns';
import * as S from './BuyPlanSection.styles';
import { CheckIcon } from '@/components/UI/Icons/Icons';
import { Chip } from '@/components/UI/Chip/Chip';
import { Drawer } from '@/components/Drawer/Drawer';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { PaymentMethods } from '@/components/Payment/PaymentMethods';
import { PaymentAgreementText } from '@/components/Payment/PaymentAgreementText';
import { Button } from '@/components/UI/Button/Button';
import { setSuffix } from '@/utils/helpers';
import { PaymentInTerminals } from '@/components/Payment/PaymentInTerminals';
import { PaymentMethod } from '@/api/types/payments.types';

const today = new Date();

export const BuyPlanSection = () => {
  const { t, i18n } = useTranslation();
  const [days, setDays] = useState(30);
  const [date, setDate] = useState(today);
  const [isOpen, toggle] = useToggleOpenState();
  const [isOpenInTerminal, toggleInTerminal] = useToggleOpenState();
  const [payment, setPayment] = useState(PaymentMethod.My_card);

  function onFinalPayClick() {
    const isTerminal = payment === PaymentMethod.Terminal;
    if (isTerminal) {
      toggleInTerminal();
    } else {
      toggle();
    }
  }

  return (
    <>
      <S.Box>
        <S.Title $variant="1">{t('pages.cabinet.package')}</S.Title>
        <S.PackageBox>
          <S.PackageName $variant="2">Gold</S.PackageName>
          <S.PackageAdv $variant="1">
            <CheckIcon />
            {t('pages.cabinet.amount_for_services', { amount: 800 })}
          </S.PackageAdv>
          <S.PackageAdv $variant="1">
            <CheckIcon />
            {t(setSuffix(i18n, String(1000), 'common.n_ads'), {
              n: 1000,
            })}
          </S.PackageAdv>
          <S.PackageAdv $variant="1">
            <CheckIcon />
            {t('pages.cabinet.discount_for_services', {
              discount: 25,
            })}
          </S.PackageAdv>
        </S.PackageBox>

        <S.Title $variant="1">{t('pages.cabinet.period')}</S.Title>
        <S.Chips>
          {[30, 60, 90].map(amount => (
            <Chip
              key={amount}
              appearance={days === amount ? 'secondaryTransparent' : 'default'}
              onClick={() => setDays(amount)}
            >
              {amount} AZN
            </Chip>
          ))}
        </S.Chips>

        <S.Title $variant="1">{t('pages.cabinet.activate_package')}</S.Title>
        <S.Chips>
          {[today, addDays(today, 20)].map(d => (
            <Chip
              key={d.getTime()}
              appearance={
                date.getTime() === d.getTime()
                  ? 'secondaryTransparent'
                  : 'default'
              }
              onClick={() => setDate(d)}
            >
              {isToday(d)
                ? t('common.today')
                : t('pages.cabinet.date_from', {
                    date: format(d, 'dd.MM.yyyy'),
                  })}
            </Chip>
          ))}
        </S.Chips>
      </S.Box>

      <S.Box>
        <S.Title $variant="1">{t('pages.cabinet.total_for_pay')}</S.Title>
        <S.TotalTable>
          <S.Row>
            <S.Title $variant="1">{t('pages.cabinet.package')}</S.Title>
            <S.Value $variant="1">Gold</S.Value>
          </S.Row>

          <S.Row>
            <S.Title $variant="1">{t('pages.cabinet.period')}</S.Title>
            <S.Value $variant="1">
              {t(setSuffix(i18n, String(30), 'common.n_days'), {
                days: 30,
              })}
            </S.Value>
          </S.Row>

          <S.Row>
            <S.Title $variant="1">{t('pages.cabinet.activation_date')}</S.Title>
            <S.Value $variant="1">
              {t('pages.cabinet.date_from', {
                date: '29.08.2023',
              })}
            </S.Value>
          </S.Row>

          <S.Row>
            <S.TotalTitle $variant="2">{t('pages.cabinet.total')}</S.TotalTitle>
            <S.TotalValue $variant="2">800 AZN</S.TotalValue>
          </S.Row>
        </S.TotalTable>
      </S.Box>

      <S.PayButton isFullWidth appearance="secondary" onClick={toggle}>
        {t('common.pay')}
      </S.PayButton>

      <Drawer
        isOpen={isOpen}
        close={toggle}
        title={t('pages.cabinet.package_name', { name: 'Gold' })}
        hasBottomMargin={false}
      >
        <S.Details $variant="4">
          {t('pages.cabinet.payment_drawer_description', {
            sum: 800,
            discount: 25,
            adsCount: 1000,
          })}
        </S.Details>

        <PaymentMethods payment={payment} setPayment={setPayment} />

        <PaymentAgreementText />

        <S.PayButtonBox>
          <Button isFullWidth appearance="secondary" onClick={onFinalPayClick}>
            {t('common.pay')}
          </Button>
        </S.PayButtonBox>
      </Drawer>

      {/* @ts-ignore */}
      <PaymentInTerminals isOpen={isOpenInTerminal} toggle={toggleInTerminal} />
    </>
  );
};
